import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    Button,
    Box,
    Text,
    Flex,
    Divider,
} from '@chakra-ui/react';
import { useContext, useState } from 'react';
import { GlobalContext } from '../../providers/Global';
import { signCheck, getMultisigAccountData } from '../../api/multisigAPI';
import { useToast } from '../../hooks/useToast';
import { getWalletErrorMessage } from '../../utils/walletErrors';
import { signScheduledTransaction } from '../../services/TransactionService';
import { buildCustomStrategy } from '../../signing/useStrategy';
import { verifyProposal } from '../../services/verifyProposal';
import { useSigningConfirmation } from '../../providers/SigningConfirmation';
import { clearKey } from '../../signing/KeyManager';
import { TransactionType } from '../../types/transactions';
import { ReactComponent as InvitationIcon } from 'assets/notification-invitation-icon.svg';

interface OwnProps {
    isOpen: boolean;
    onClose: () => void;
    walletName: string;
    walletId: string;
    /** The wallet card's account: the invitation is checked against it, not its own claim */
    walletAccountId: string;
    transaction: TransactionType;
}

const InvitationSigningModal = ({ isOpen, onClose, walletName, walletId, walletAccountId, transaction }: OwnProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const { showToast } = useToast();
    const { requestSignConfirmation } = useSigningConfirmation();
    const {
        connection: { accountId, hashconnectConnectorInstance },
        connectedNetwork,
        updateAccount,
    } = useContext(GlobalContext);

    const handleJoin = async () => {
        if (!accountId || !connectedNetwork) {
            showToast('Connection error', 'error');
            return;
        }

        setIsLoading(true);
        try {
            // The schedule, not the invitation's text, is what executes: check
            // it on the mirror node before signing.
            const verdict = await verifyProposal(transaction, {
                walletAccountId,
                network: connectedNetwork,
                signerAccountId: accountId,
                joining: true,
            });
            if (!verdict.ok) {
                // Handled below like the network's own SCHEDULE_ALREADY_EXECUTED
                if (verdict.alreadyExecuted) throw new Error('SCHEDULE_ALREADY_EXECUTED');
                showToast(`Not signed: ${verdict.reason}`, 'error');
                setIsLoading(false);
                return;
            }

            const isCustom = connectedNetwork?.startsWith('custom-');

            if (isCustom) {
                const confirmed = await requestSignConfirmation({
                    type: 'Join Wallet',
                    items: [{ label: 'Wallet', value: walletName }, ...verdict.items],
                });
                if (!confirmed) {
                    setIsLoading(false);
                    return;
                }
            }

            // Build strategy AFTER confirmation (key is freshly decrypted)
            const strategy = buildCustomStrategy(connectedNetwork, accountId);
            let signedTx;
            if (strategy) {
                signedTx = await signScheduledTransaction(strategy, transaction.schedule_id);
            } else {
                if (!hashconnectConnectorInstance) {
                    showToast('Wallet connector not initialized', 'error');
                    return;
                }
                signedTx = await hashconnectConnectorInstance.signScheduledTransaction(
                    transaction.schedule_id,
                    accountId,
                    connectedNetwork
                );
            }

            if (signedTx) {
                const txIdStr = 'txId' in signedTx ? signedTx.txId : signedTx.transactionId?.toString();
                await signCheck(walletId, txIdStr, transaction._id, accountId);
                showToast('Successfully signed invitation!', 'success');
                onClose();
                const updatedData = await getMultisigAccountData(walletId!);
                if (updatedData?.data) {
                    updateAccount(updatedData.data);
                }
            }
        } catch (error: unknown) {
            const errorMsg = error instanceof Error ? error.message : String(error);
            const isAlreadyExecuted = errorMsg.includes('SCHEDULE_ALREADY_EXECUTED');

            if (isAlreadyExecuted) {
                // The ThresholdUpdate already executed on Hedera (existing signers
                // met the on-chain threshold). The new signer is already added —
                // just refresh wallet data and proceed.
                showToast('Wallet configuration already applied — joining now!', 'success');
                onClose();
                const updatedData = await getMultisigAccountData(walletId!);
                if (updatedData?.data) {
                    updateAccount(updatedData.data);
                }
            } else {
                console.error('Failed to sign invitation:', error);
                showToast(getWalletErrorMessage(error, 'Failed to sign invitation'), 'error');
            }
        } finally {
            clearKey();
            setIsLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
            <ModalOverlay />
            <ModalContent maxWidth="480px">
                <ModalHeader textAlign="center" pt={8}>
                    <Text size="xl" variant="bodyBold">
                        Wallet Invitation
                    </Text>
                    <ModalCloseButton />
                </ModalHeader>
                <ModalBody pb={8} px={8}>
                    <Flex direction="column" gap={6} alignItems="center">
                        <Flex
                            alignItems="center"
                            justifyContent="center"
                            w={16}
                            h={16}
                            borderRadius="full"
                            bg="blue.50"
                        >
                            <InvitationIcon width={32} height={32} />
                        </Flex>

                        <Box textAlign="center">
                            <Text color="blackAlpha.600" mb={1}>
                                You have been invited to join
                            </Text>
                            <Text size="lg" variant="bodyBold">
                                {walletName}
                            </Text>
                        </Box>

                        <Divider />

                        <Box w="full" bg="primary" p={4} borderRadius={8}>
                            <Text size="xs" color="blackAlpha.600" mb={1}>
                                WALLET ADDRESS
                            </Text>
                            <Text size="sm" variant="bodyBold">
                                {walletAccountId}
                            </Text>
                        </Box>

                        <Box w="full" bg="primary" p={4} borderRadius={8}>
                            <Text size="xs" color="blackAlpha.600" mb={1}>
                                PROPOSED CONFIGURATION
                            </Text>
                            <Text size="sm" variant="bodyBold">
                                {(transaction.params as any).newThreshold} of {(transaction.params as any).newSigners?.length} signers
                            </Text>
                        </Box>

                        <Text fontSize="xs" color="blackAlpha.500" textAlign="center">
                            By clicking "Join Wallet", you will sign the transaction to add your key as one of the multisig owners.
                        </Text>

                        <Button
                            variant="primary"
                            w="full"
                            h="48px"
                            onClick={handleJoin}
                            isLoading={isLoading}
                            loadingText="Signing..."
                        >
                            Join Wallet
                        </Button>
                    </Flex>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default InvitationSigningModal;
